import { ENUM_FALLBACK } from "@clipboard-health/contract-core";
import {
  type GetSpendingControlsV2Response,
  type SpendingControlUsageDto,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";

import { type SpendingControlWithUsage } from "../types";

/**
 * Joins each v2 spending-control row with its usage (matched via
 * `relationships.usage.data.id`) and flattens it into {@link SpendingControlWithUsage}.
 */
export function joinSpendingControlsWithUsage(
  response: GetSpendingControlsV2Response | undefined
): SpendingControlWithUsage[] {
  if (!isDefined(response)) {
    return [];
  }

  const usageById = new Map<string, SpendingControlUsageDto>(
    (response.included ?? []).map((usage) => [usage.id, usage])
  );

  return response.data.flatMap((control) => {
    // The contract models controlType with a forward-compat fallback for unknown backend values;
    // skip those rows since the UI only renders known control types.
    const { controlType } = control.attributes;
    if (controlType === ENUM_FALLBACK) {
      return [];
    }

    const usageId = control.relationships.usage?.data?.id;
    const usageAttributes = isDefined(usageId) ? usageById.get(usageId)?.attributes : undefined;

    return {
      id: control.id,
      workplaceId: control.relationships.workplace.data.id,
      controlType,
      // minor units == cents (USD); absent when no limit is set.
      amountInCents: control.attributes.amount?.amountInMinorUnits,
      amountInHours: control.attributes.amountInHours,
      startDate: control.attributes.startDate,
      endDate: control.attributes.endDate,
      createdAt: control.attributes.controlCreatedAt,
      updatedAt: control.attributes.controlUpdatedAt,
      verifiedAmountInCents: usageAttributes?.verifiedAmount?.amountInMinorUnits ?? 0,
      plannedAmountInCents: usageAttributes?.plannedAmount?.amountInMinorUnits ?? 0,
      verifiedHours: usageAttributes?.verifiedHours ?? 0,
      plannedHours: usageAttributes?.plannedHours ?? 0,
    };
  });
}
